import React from 'react'
import clsx from 'clsx'
import { AppButton } from '@easyllama/ui-components'
import { LlamaLoading } from '@easyllama/ui-components/icons'
import { Card, CardContent } from '~/atoms/shadcn/card'
import PlanCounter from './PlanCounter'
import { getSeatRules } from '~/service/Billing/billingConstants'
import { useApp } from '~/context/appContext'

export default function PlanCard({ plan, count, onCountChange, onSelect, onContactSales, isSelected, isSelectLoading = false, isSelectDisabled = false, isHighlightCard = false, isCounterDisabled = false, isSelectCurrentPlanDisabled = false, subscription = null, hideCounterSubtext = false }) {
  const { company } = useApp()
  const { minCount, step } = getSeatRules(company)
  const isCurrentCard = plan.isCurrent
  const hasNoSubscriptionOrCanceled = !subscription || subscription.status === 'canceled'
  const hasHighLicenses = !hasNoSubscriptionOrCanceled && subscription && (subscription.licensesCount || 0) >= minCount
  const counterMinCount = hasHighLicenses ? 0 : minCount

  // gap-0/rounded-2xl/text-inherit/border-0 each cancel a shadcn Card default this card never used
  // (gap-6, rounded-lg, text-card-foreground, and the hairline the gradient variants replace).
  const cardClassName = clsx(
    'relative flex flex-col gap-0 rounded-2xl text-inherit transition-all overflow-hidden',
    isHighlightCard
      ? 'cursor-default border-0 bg-llama-grey-9 h-full'
      : isSelected
        ? 'border-0 bg-white bg-linear-to-b from-[rgba(151,56,255,0.05)] to-[rgba(0,179,215,0.05)] h-full'
        : 'bg-white border border-neutral-300',
  )

  const card = (
    <Card
      className={cardClassName}
    >
      {/* data-safari-current-badge: without `absolute` restored, the badge falls into flow and pushes
          the card down instead of overlaying the pt-10 left for it. */}
      {isCurrentCard && (
        <div className='absolute top-0 inset-x-0 flex items-center justify-center h-8 px-3 text-xs font-semibold text-center tracking-widest uppercase select-none bg-llama-purple-5 text-white' data-safari-current-badge>
          Current
        </div>
      )}
      {/* Name + price + description — fixed heights keep counter/button rows pixel-aligned across all cards.
          CardContent, not CardHeader: that one is a grid whose gap-1.5 would space these three blocks
          apart, and it adds a container-query context this card never queries. */}
      <CardContent className='pt-10 pb-8 h-[240px] max-lg:h-auto' data-safari-pt-10 data-safari-pb-8>
        {/* fontFamily inline, not just the font-[...] class: inside PlansDialog, Safari drops the
            layered Tailwind font-family after the announcement scope's revert-layer reset (Chrome
            keeps it) - an inline style always wins over any stylesheet rule, revert-layer included. */}
        <h1 title={plan.name} className='mb-3 line-clamp-2 overflow-hidden text-[32px] font-semibold leading-snug lining-nums text-llama-grey-7' style={{ fontFamily: 'RecoletaSemiBold' }}>
          {plan.name}
        </h1>
        <div className='flex items-baseline gap-0.5 mb-4'>
          <span className='text-4xl font-bold lining-nums text-llama-grey-7 max-sm:text-3xl' style={{ fontFamily: 'RecoletaBold' }}>
            {plan.price}
          </span>
          <span className='text-base text-llama-grey-7'>
            <span className='font-bold'>{plan.periodUnit}</span>{plan.periodCycle}
          </span>
        </div>
        <p title={plan.description} className='line-clamp-3 overflow-hidden text-base font-normal font-jakarta leading-[150%] lining-nums text-llama-grey-7'>
          {plan.description}
        </p>
      </CardContent>

      {/* Counter */}
      <CardContent>
        <p className='text-sm font-normal mb-2 text-llama-grey-7'>
          How many <span className='font-bold'>{plan.counterLabel}</span> would you like to add?
        </p>
        <div className={hideCounterSubtext ? 'mb-6' : ''}>
          <PlanCounter value={count} onChange={onCountChange} disabled={isCounterDisabled || !plan.switchable} minCount={counterMinCount} step={step} />
        </div>
        {!hideCounterSubtext && (
          <p className="mt-2 mb-6 flex h-8 flex-col items-center justify-center text-center text-xs text-llama-grey-7">
            <span>{`(${minCount}-${['core', 'ultimate', 'unlimited'].includes(plan.id) ? 'seat' : 'course'} minimum,`}</span>
            <span>{`sold in increments of ${step}.)`}</span>
          </p>
        )}
      </CardContent>

      {/* Select button */}
      {(() => {
        const isNonSwitchableCurrent = isCurrentCard && !plan.switchable
        // When the host screen suppresses the contact-sales dialog (showContactSales={false}),
        // BillingPlansCards omits onContactSales — without this, the button would still render
        // as a live CTA that silently no-ops on click.
        const isContactSalesUnavailable = !plan.switchable && !isCurrentCard && !onContactSales
        const minCountForButton = isCurrentCard && hasHighLicenses ? 0 : (!isCurrentCard && hasHighLicenses ? 0 : minCount)
        const isDisabled = isNonSwitchableCurrent || isContactSalesUnavailable || isSelectLoading || isSelectDisabled || (plan.switchable && count < minCountForButton) || isSelectCurrentPlanDisabled
        return (
          <CardContent className={isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}>
            <AppButton
              size='lg'
              variant={isNonSwitchableCurrent ? 'outline' : 'default'}
              className='w-full'
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation()
                if (!plan.switchable) { onContactSales?.(); return }
                if (count >= minCountForButton) onSelect(plan)
              }}
              title={isSelectCurrentPlanDisabled ? 'Please increase seat quantity to proceed' : ''}
            >
              {isSelectLoading && <LlamaLoading className='size-4 animate-spin mr-1' />}
              {isNonSwitchableCurrent ? 'Current plan' : !plan.switchable ? 'Contact sales' : `Select${count > 0 ? ` (${count})` : ''}`}
            </AppButton>
          </CardContent>
        )
      })()}

      {/* Divider */}
      <div className='border-t border-neutral-300 my-8 mx-6' />

      {/* Feature list or legacy message */}
      <CardContent className='pb-8 flex-1' data-safari-pb-8>
        {plan.legacyMessage ? (
          <p className='text-sm font-semibold leading-relaxed text-llama-grey-7'>
            {plan.legacyMessage}
          </p>
        ) : (
          <>
            {plan.featureHeader && (
              <p className='text-base font-bold mb-3 text-llama-grey-7'>
                {plan.featureHeader}
              </p>
            )}
            <ul className='list-none p-0 space-y-4'>
              {plan.featureList.map((feature, i) => (
                <li key={i} className='text-sm leading-snug text-llama-grey-7 font-semibold'>
                  {feature}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  )

  if (isSelected && !isHighlightCard) {
    return (
      <div
        className='rounded-[20px] overflow-hidden'
        style={{
          background: 'linear-gradient(180deg, #9738FF 0%, #00B3D7 100%)',
          padding: '4px',
        }}
      >
        {card}
      </div>
    )
  }

  if (isHighlightCard) {
    const topColor = isCurrentCard ? 'var(--color-llama-purple-5)' : 'var(--color-llama-grey-8)'
    return (
      <div
        className='rounded-[17px] overflow-hidden'
        style={{
          background: `linear-gradient(to bottom, ${topColor} 33px, var(--color-llama-grey-8) 33px)`,
          padding: '1px',
        }}
      >
        {card}
      </div>
    )
  }

  return card
}
